/**
 * Builds a human, location-based default title for a saved Spatial Analysis report.
 *
 * Priority:
 *   1. A meaningful user-given polygon name (anything other than the "New Polygon" default).
 *   2. A reverse-geocoded place name for the study-area centroid ("Capitol Hill, Seattle").
 *   3. The census geography label when present (US ACS).
 *   4. The centroid coordinates.
 *   5. The provided base title (already translated).
 *
 * Best-effort and never throws — the reverse-geocode is network-dependent, and the
 * user can rename the report afterwards.
 */
import { reverseGeocode } from '../geocoding';
import { SpatialAnalysisMetrics, SpatialAnalysisSource } from './types';

const DEFAULT_POLYGON_NAME = 'New Polygon';

async function deriveLocationLabel(metrics: SpatialAnalysisMetrics | undefined): Promise<string | null> {
  const centroid = metrics?.studyArea?.centroid;
  const hasCentroid = !!centroid && Number.isFinite(centroid.lat) && Number.isFinite(centroid.lng);

  if (hasCentroid) {
    const place = await reverseGeocode(centroid!.lat, centroid!.lng);
    if (place?.shortName) {
      return place.shortName;
    }
  }

  const censusGeography = metrics?.population?.censusGeography?.trim();
  if (censusGeography) {
    return censusGeography;
  }

  if (hasCentroid) {
    return `${centroid!.lat.toFixed(3)}, ${centroid!.lng.toFixed(3)}`;
  }

  return null;
}

export async function buildSpatialAnalysisReportTitle(
  source: SpatialAnalysisSource | undefined,
  metrics: SpatialAnalysisMetrics | undefined,
  baseTitle: string,
): Promise<string> {
  const polygonName = source?.polygonName?.trim();
  if (polygonName && polygonName !== DEFAULT_POLYGON_NAME) {
    return polygonName;
  }

  const label = await deriveLocationLabel(metrics);
  return label ? `${baseTitle} – ${label}` : baseTitle;
}
